import {
  Button,
  DialogFooter,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Modal,
  SelectField,
} from "@peer-start/ui/reducate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { mentorMeetingPassed, type StartingTeacherProgress } from "../data/starting-teacher-route.js";
import type { MentorSettings } from "../types/starting-teacher-mentor.js";

const listedMentorNames = ["Femke Kuipers", "Marieke Bos"] as const;
const mentorNotListed = "not-listed";

function todayValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function isListedMentor(name: string | null): name is (typeof listedMentorNames)[number] {
  return listedMentorNames.some((listed) => listed === name);
}

function mentorChoice(name: string | null, hasMentor: boolean): string {
  if (isListedMentor(name)) return name;
  if (hasMentor || name) return mentorNotListed;
  return "";
}

function customMentorName(name: string | null): string {
  return isListedMentor(name) || !name ? "" : name;
}

const mentorFrequencies = ["weekly", "fortnightly", "monthly", "quarterly", "none"] as const;

function meetingDateValue(date: string | null, frequency: StartingTeacherProgress["mentorFrequency"]): string {
  if (frequency === "none" && mentorMeetingPassed(date)) return "";
  return date ?? "";
}

export function StartingTeacherMentorModal({
  open,
  progress,
  onClose,
  onSave,
}: {
  open: boolean;
  progress: StartingTeacherProgress;
  onClose: () => void;
  onSave: (settings: MentorSettings) => void;
}) {
  const { t } = useTranslation();
  const [mentorChoiceValue, setMentorChoiceValue] = useState(mentorChoice(progress.mentorName, progress.hasMentor));
  const [customName, setCustomName] = useState(customMentorName(progress.mentorName));
  const [meetingDate, setMeetingDate] = useState(
    meetingDateValue(progress.mentorMeetingDate, progress.mentorFrequency),
  );
  const [frequency, setFrequency] = useState<NonNullable<StartingTeacherProgress["mentorFrequency"]> | "">(
    progress.mentorFrequency ?? "",
  );

  useEffect(() => {
    if (!open) return;
    setMentorChoiceValue(mentorChoice(progress.mentorName, progress.hasMentor));
    setCustomName(customMentorName(progress.mentorName));
    setMeetingDate(meetingDateValue(progress.mentorMeetingDate, progress.mentorFrequency));
    setFrequency(progress.mentorFrequency ?? "");
  }, [open, progress.hasMentor, progress.mentorFrequency, progress.mentorMeetingDate, progress.mentorName]);

  const notListed = mentorChoiceValue === mentorNotListed;
  const dateStillValid = Boolean(meetingDate) && !(frequency === "none" && mentorMeetingPassed(meetingDate));
  const complete = Boolean(mentorChoiceValue && dateStillValid && frequency);

  return (
    <Modal
      closeLabel={t("common.close")}
      controlId="starting-teacher.mentor.setup"
      description={t("startingTeacher.mentorSetupBody")}
      onClose={onClose}
      open={open}
      title={t("startingTeacher.mentorSetupTitle")}
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="starting-teacher-mentor-name">{t("startingTeacher.mentorName")}</FieldLabel>
          <SelectField
            controlId="starting-teacher.mentor.setup.name"
            id="starting-teacher-mentor-name"
            onValueChange={setMentorChoiceValue}
            options={[
              ...listedMentorNames.map((name) => ({ value: name, label: name })),
              { value: mentorNotListed, label: t("startingTeacher.mentorNotListed") },
            ]}
            placeholder={t("startingTeacher.mentorNamePlaceholder")}
            value={mentorChoiceValue}
          />
        </Field>
        {notListed ? (
          <Field>
            <FieldLabel htmlFor="starting-teacher-mentor-custom-name">
              {t("startingTeacher.mentorCustomName")}
            </FieldLabel>
            <Input
              data-control-id="starting-teacher.mentor.setup.custom-name"
              id="starting-teacher-mentor-custom-name"
              onChange={(event) => setCustomName(event.target.value)}
              placeholder={t("startingTeacher.mentorCustomNamePlaceholder")}
              value={customName}
            />
          </Field>
        ) : null}
        <Field>
          <FieldLabel htmlFor="starting-teacher-mentor-date">
            {t("startingTeacher.mentorMeetingDate")}
          </FieldLabel>
          <Input
            className="min-h-11"
            data-control-id="starting-teacher.mentor.setup.date"
            id="starting-teacher-mentor-date"
            min={todayValue()}
            onChange={(event) => setMeetingDate(event.target.value)}
            type="date"
            value={meetingDate}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="starting-teacher-mentor-frequency">
            {t("startingTeacher.mentorFrequency")}
          </FieldLabel>
          <SelectField
            controlId="starting-teacher.mentor.setup.frequency"
            id="starting-teacher-mentor-frequency"
            describedBy="starting-teacher-mentor-frequency-hint"
            onValueChange={(value) =>
              setFrequency(value as NonNullable<StartingTeacherProgress["mentorFrequency"]>)
            }
            options={mentorFrequencies.map((value) => ({
              value,
              label: t(`startingTeacher.mentorFrequencies.${value}`),
            }))}
            placeholder={t("startingTeacher.mentorFrequencyPlaceholder")}
            value={frequency}
          />
          <FieldDescription id="starting-teacher-mentor-frequency-hint">
            {t("startingTeacher.mentorFrequencyHint")}
          </FieldDescription>
        </Field>
      </FieldGroup>
      <DialogFooter>
        <Button
          controlId="starting-teacher.mentor.setup.cancel"
          onClick={onClose}
          type="button"
          variant="secondary"
        >
          {t("common.cancel")}
        </Button>
        <Button
          controlId="starting-teacher.mentor.setup.save"
          disabled={!complete}
          onClick={() => {
            if (!mentorChoiceValue || !dateStillValid || !frequency) return;
            const mentorName = notListed ? customName.trim() || null : mentorChoiceValue;
            onSave({ mentorName, mentorMeetingDate: meetingDate, mentorFrequency: frequency });
          }}
          type="button"
        >
          {t("startingTeacher.mentorSave")}
        </Button>
      </DialogFooter>
    </Modal>
  );
}
